import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { Automation, NOT_FOUND_IN_CDT } from '@core/typings/ui/automation.typing';
import { PicklistDataType } from '@features/custom-data-tables/custom-data-tables.typing';
import { CustomDataTablesService } from '@features/custom-data-tables/services/custom-data-table.service';
import { TypeSafeFormBuilder, TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { RuleGroup } from '../automation-rule/automation-rule.component';
import { RuleAutomationService } from '../rule-automation.service';

@Component({
  selector: 'gc-automation-criteria-value',
  templateUrl: './automation-criteria-value.component.html',
  styleUrls: ['./automation-criteria-value.component.scss']
})
export class AutomationCriteriaValueComponent implements OnInit, OnChanges {
  private _valuesHidden: boolean;

  @Input()
  public get valuesHidden (): boolean {
    return this._valuesHidden;
  }
  public set valuesHidden (value: boolean) {
    this._valuesHidden = value;
    if (this.hasInitialized) {
      this.setRule();
    }
  }
  @Input() criteriaFormGroup: TypeSafeFormGroup<RuleGroup>;
  @Input() currentComparison: Automation.Comparisons;
  @Input() columnComparisonOptions: TypeaheadSelectOption<string>[] = [];
  @Output() removeRule = new EventEmitter();

  rule: Automation.CriteriaFormState;
  valuesFormArray: UntypedFormArray;
  Comparisons = Automation.Comparisons;
  Majorities = Automation.MajorityOptions;
  negatedValue: Automation.NegatedType;
  firstIndex: number;
  totalIndex: number;
  hasInitialized = false;
  isBetween = false;
  isCdtComparison: boolean;
  fileUploadedOptions = this.ruleAutomationService.getFileUploadOptions();
  selectedOptions = this.ruleAutomationService.getSelectedOptions();
  numberIsOptions = this.ruleAutomationService.getNumberIsOptions();
  comparisonNegateOptions = this.ruleAutomationService.getComparisonNegateOptions();
  majorityOptions = this.ruleAutomationService.getMajorityOptions();
  dateOptions = this.ruleAutomationService.getDateOptions();
  numberRangeOptions = this.ruleAutomationService.getNumberRangeOptions();
  cdtComparisonOptions = this.customDataTableService.pickListIdTypeaheadOptions.map((item) => {
    return {
      label: item.label,
      value: `${item.value}`
    };
  });
  cdtComparisonNumberOptions = this.customDataTableService.pickListTypeaheadOptions.filter((cdt) => {
    return cdt.value.dataType === PicklistDataType.Numeric;
  }).map((item) => {
    return {
      label: item.label,
      value: `${item.value.id}`
    };
  });

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private ruleAutomationService: RuleAutomationService,
    private customDataTableService: CustomDataTablesService
  ) { }

  get showOrButton () {
    return this.totalIndex > 0 &&
      (
        this.currentComparison === Automation.Comparisons.TextIs ||
        this.currentComparison === Automation.Comparisons.SelectIs
      );
  }

  get isNegated () {
    return this.negatedValue === true ||
      this.negatedValue === NOT_FOUND_IN_CDT;
  }

  ngOnInit () {
    this.valuesFormArray = this.criteriaFormGroup.get('values') as UntypedFormArray;
    this.setRule();
    this.hasInitialized = true;
  }

  ngOnChanges () {
    if (this.hasInitialized) {
      this.setRule();
    }
  }

  setRule () {
    this.rule = this.criteriaFormGroup.value;
    this.flushRemovedValuesAndSetIndeces();
    const firstVisibleValue = this.valuesFormArray.at(this.firstIndex);
    this.setNegatedVal(firstVisibleValue?.value?.comparisonNegated ?? false);
    const isDateOrNumber = [
      Automation.Comparisons.DateRange,
      Automation.Comparisons.NumberRange
    ].includes(this.currentComparison);
    const isDefaultComparison = firstVisibleValue?.value?.comparisonType === Automation.CriteriaComparisonTypes.Default;
    this.isBetween = !this.isCdtComparison && isDefaultComparison && isDateOrNumber;
  }

  setNegatedVal (negatedVal: Automation.NegatedType) {
    this.negatedValue = negatedVal;
    this.isCdtComparison = this.ruleAutomationService.isCdtTypeRule(this.negatedValue);
  }

  negateChange (val: Automation.NegatedType) {
    const wasCdt = this.isCdtComparison;
    this.setNegatedVal(val);
    const isCdt = this.isCdtComparison;
    if ((wasCdt && !isCdt) || (isCdt && !wasCdt)) {
      this.ruleAutomationService.resetValues(this.valuesFormArray, this.negatedValue);
      this.setRule();
    }
  }

  addValue () {
    const newValue: Automation.CriteriaValueFormState = {
      comparisonGreaterThan: null,
      comparisonType: Automation.CriteriaComparisonTypes.Default,
      comparisonLessThan: null,
      comparisonNegated: false,
      comparisonValue: null,
      isRemoved: false,
      greaterThanId: null,
      lessThanId: null,
      valueId: null
    };
    this.valuesFormArray.push(this.formBuilder.group(newValue));
    this.setRule();
  }

  removeValue (index: number) {
    this.valuesFormArray.at(index).get('isRemoved').setValue(true);
    this.setRule();

    if (this.totalIndex === 0) {
      this.removeRule.emit();
    }
  }

  flushRemovedValuesAndSetIndeces () {
    for (let index = this.valuesFormArray.length; index --> 0;) {
      const value = this.valuesFormArray.at(index).value;
      const shouldPurge = value.isRemoved && !(value.valueId || value.lessThanId || value.greaterThanId);

      if (shouldPurge) {
        this.valuesFormArray.removeAt(index);
      }
    }

    this.rule.values = [...this.valuesFormArray.value];
    this.setIndices();
  }

  setIndices () {
    this.firstIndex = this.rule.values.findIndex(val => !val.isRemoved);
    this.totalIndex = this.rule.values.reduce((acc, value) => !value.isRemoved ? acc + 1 : acc, 0);
  }
}
